import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';

import {
  formatFinanceLiquidityRetention,
  formatFinanceMoney,
  isActiveFinanceBankruptcy,
  sortFinanceProfileYears,
} from '../utils/monitoringResultsUtils';

import type IMonitoringFinanceEntityCard from './interfaces/IMonitoringFinanceEntityCard';

import styles from './sass/MonitoringFinanceEntityCard.module.scss';

const getScoreClassName = (score: number): string => {
  if (score < 40) {
    return styles.scoreLow;
  }

  if (score < 70) {
    return styles.scoreMid;
  }

  return styles.scoreHigh;
};

function MonitoringFinanceEntityCard({
  entity,
  defaultExpanded = isActiveFinanceBankruptcy(entity) || entity.stability_score < 40,
}: IMonitoringFinanceEntityCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);
  const currency = entity.currency || 'USD';
  const years = sortFinanceProfileYears(entity.financial_profile?.years || []);
  const isProtectionActive = isActiveFinanceBankruptcy(entity);
  const trendKey = entity.trend === 'declining' || entity.trend === 'improving' || entity.trend === 'stable'
    ? entity.trend
    : 'stable';

  return (
    <article
      className={clsx(styles.container, {
        [styles.protectionActive]: isProtectionActive,
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          <h3 className={styles.title}>{entity.name}</h3>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, getScoreClassName(entity.stability_score))}>
              {t('valueChainMap.monitoring.results.financeStabilityScore', {
                value: entity.stability_score,
              })}
            </span>
            <span className={clsx(styles.badge, styles[`trend_${trendKey}`])}>
              {t(`valueChainMap.monitoring.results.financeTrend.${trendKey}`)}
            </span>
            {isProtectionActive ? (
              <span className={clsx(styles.badge, styles.protectionBadge)}>
                {entity.bankruptcy_status?.label
                  || t('valueChainMap.monitoring.results.financeProtectionActive')}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          <p className={styles.profileCaption}>
            {t('valueChainMap.monitoring.results.financeProfileCaption')}
          </p>

          {years.length ? (
            <div className={styles.profileTableWrap}>
              <table className={styles.profileTable}>
                <thead>
                  <tr>
                    <th>{t('valueChainMap.monitoring.results.financeYear')}</th>
                    <th>{t('valueChainMap.monitoring.results.financeRevenue')}</th>
                    <th>{t('valueChainMap.monitoring.results.financeEbitda')}</th>
                    <th>{t('valueChainMap.monitoring.results.financeLiquidityRetention')}</th>
                  </tr>
                </thead>
                <tbody>
                  {years.map((year) => {
                    const liquidity = year.liquidity_retention;
                    const isPressure = typeof liquidity === 'number' && liquidity < 1;

                    return (
                      <tr key={`${entity.unitId}-${year.year_index}`}>
                        <td>
                          {year.fiscal_year ?? `Y${year.year_index}`}
                        </td>
                        <td>{formatFinanceMoney(year.revenue, currency)}</td>
                        <td>{formatFinanceMoney(year.ebitda, currency)}</td>
                        <td className={clsx({ [styles.liquidityPressure]: isPressure })}>
                          {formatFinanceLiquidityRetention(liquidity)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : null}

          {(entity.alerts || []).length ? (
            <div className={styles.alertsList}>
              {entity.alerts?.map((alert) => (
                <div
                  key={`${entity.unitId}-${alert.title}`}
                  className={clsx(styles.alertItem, {
                    [styles.alertHigh]: alert.severity === 'high',
                    [styles.alertCritical]: alert.severity === 'critical',
                  })}
                >
                  <div className={styles.alertTitle}>{alert.title}</div>
                  <div className={styles.alertMeta}>
                    {alert.theme}
                    {alert.source ? ` · ${alert.source}` : ''}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringFinanceEntityCard;
